/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import {ClientError, gql} from 'graphql-request';
import {GenericObject} from '@ion/core/src/types/entities';
import {getClient} from './GraphQLConfig';
import * as logger from './GraphQLLogger';
import {
  compress,
  createCombinedQuery,
  CreateCombinedQueryParams,
  createMutation,
  createQuery,
  CreateQueryParams
} from './Query';
import {generatePlatformsQuery} from './QueryGenerators';
import {
  Account,
  Application,
  Asset,
  CurrentUser,
  Device,
  GetAccountArgs,
  GetApplicationArgs,
  Group,
  Login,
  Mutation,
  Platform,
  Query,
  Role,
  SearchAssetArgs,
  SearchDeviceArgs,
  SearchGroupArgs,
  SearchPlatformsArgs,
  SearchRoleArgs,
  SearchUserArgs,
  User,
  UserLogin
} from './types';

/**
 * Defines the `RequestError` which is thrown when a query or mutation request
 * fails.
 */
export type RequestError<T> = {
  errors: string[]
  cause: ClientError
  results: T
}

/**
 * Defines the `Login` typed Query and Response implementation.
 */
export type LoginParams = CreateQueryParams<
UserLogin, Login
>

/**
 * Defines the `CurrentUser` typed Query and Response implementation.
 */
export type CurrentUserParams = CreateQueryParams<
CurrentUser
>

/**
 * Defines the `UserSearch` typed Query and Response implementation.
 */
export type SearchUsersParams = CreateQueryParams<
CurrentUser, null, SearchUserArgs
>

/**
 * Defines the `DevicePlatformQuery` typed Query and Response implementation.
 */
export type DevicePlatformQueryParams = CreateQueryParams<
Platform, null, SearchPlatformsArgs
>

/**
 * Defines the `RoleSearch` typed Query and Response implementation.
 */
export type SearchRolesParams = CreateQueryParams<
Role, null, SearchRoleArgs
>

/**
 * Defines the `DeviceSearch` typed Query and Response implementation.
 */
export type SearchDevicesParams = CreateQueryParams<
Device, null, SearchDeviceArgs
>

/**
 * Defines the `AssetSearch` typed Query and Response implementation.
 */
export type SearchAssetsParams = CreateQueryParams<
Asset, null, SearchAssetArgs
>

/**
 * Defines the `GroupSearch` typed Query and Response implementation.
 */
export type SearchGroupsParams = CreateQueryParams<
Group, null, SearchGroupArgs
>

/**
 * Defines the `AccountQuery` (`getAccount`) typed Query and Response.
 */
export type GetAccountParams = CreateQueryParams<
Account, GetAccountArgs
>

/**
 * Defines the `ApplicationQuery` (`getApplication`) typed Query and Response.
 */
export type GetApplicationParams = CreateQueryParams<
Application, GetApplicationArgs
>

/**
 * Provides the mutation for authenticating the given `user` and retrieving the
 * authToken and current user.
 *
 * ```typescript
 * import {login} from '@ion/services/src/graphql/GraphQL';
 *
 * const user = await login({
 *   args: {
 *     username: 'some.username',
 *     password: 'some.password
 *   },
 *   fields: ['authToken', {
 *     user : ['id', 'username']
 *   }]
 * });
 * ```
 *
 * @see     {@link LoginParams}
 * @see     {@link UserLogin}
 * @param   params An object containing the args to be used in the request,
 *          and the fields to be returned in the response.
 * @returns A Promise containing the requested `UserLogin` options.
 */
export const login = async(params: LoginParams): Promise<Partial<UserLogin>> => {
  const result = await mutationRequest<Partial<UserLogin>>('Login', params);
  return result;
};

/**
 * Provides the query for retrieving the current user (authenticated) `user`.
 *
 * ```typescript
 * import {currentUser} from '@ion/services/src/graphql/GraphQL';
 *
 * const user = await currentUser({
 *   fields: ['username', 'email', {
 *     extendedAttribute : ['value']
 *   }]
 * });
 * ```
 *
 * @see     {@link CurrentUserParams}
 * @see     {@link CurrentUser}
 * @param   params An object containing the fields to be returned in the response.
 * @returns A Promise containing the `CurrentUser` entity.
 */
export const currentUser = async(params: CurrentUserParams
): Promise<Partial<CurrentUser>> => {
  const result = await queryRequest<Partial<CurrentUser>>('CurrentUser', params);
  return result;
};

/**
 * Retrieves a list of Users based on the provided search filters.
 *
 * ```typescript
 * import {searchUsers} from '@ion/services/src/graphql/GraphQL';
 *
 * const users = await searchUsers({
 *   search : {
 *     filter : {username: 'efeminella.adm'}
 *   },
 *   fields : ['username', {
 *     roles : ['id']
 *   }]
 * });
 * ```
 *
 * @see     {@link SearchUsersParams}
 * @see     {@link User}
 * @param   params An object containing the fields to be returned in the response,
 *          and the search criteria from which the results are to be retrieved.
 * @returns A Promise containing an array of matching `User` entities.
 */
export const searchUsers = async(
  params: SearchUsersParams): Promise<Partial<User[]>> => {
  const result = await queryRequest<{items: Partial<User>[]}>('SearchUser', params);
  return result?.items;
};

export const getDevicePlatforms = async(
  params: DevicePlatformQueryParams): Promise<Partial<Platform[]>> => {
  const result = await queryRequest<{platforms: Promise<Partial<Platform[]>>}>('DevicePlatformQuery', params, generatePlatformsQuery);
  return result?.platforms;
};

/**
 * Retrieves a list of Roles based on the provided search filters.
 *
 * ```typescript
 * import {searchRoles} from '@ion/services/src/graphql/GraphQL';
 *
 * const roles = await searchRoles({
 *   search : {
 *     filter : {name: 'ion'}
 *   },
 *   fields: ['id', 'name']
 * });
 * ```
 *
 * @see     {@link SearchRolesParams}
 * @see     {@link Role}
 * @param   params An object containing the fields to be returned in the response,
 *          and the search criteria from which the results are to be retrieved.
 * @returns A Promise containing an array of matching `Role` entities.
 */
export const searchRoles = async(
  params: SearchRolesParams): Promise<Partial<Role[]>> => {
  const result = await queryRequest<{items: Partial<Role>[]}>('SearchRole', params);
  return result?.items;
};

/**
 * Retrieves a list of Devices based on the provided search filters.
 *
 * ```typescript
 * import {searchDevices} from '@ion/services/src/graphql/GraphQL';
 *
 * const devices = await searchDevices({
 *   search : {
 *     filter : {name: 'ion'}
 *   },
 *   fields: ['id', 'esn']
 * });
 * ```
 *
 * @see     {@link SearchDevicesParams}
 * @see     {@link Device}
 * @param   params An object containing the fields to be returned in the response,
 *          and the search criteria from which the results are to be retrieved.
 * @returns A Promise containing an array of matching `Device` entities.
 */
export const searchDevices = async(
  params: SearchDevicesParams): Promise<Partial<Device[]>> => {
  const result = await queryRequest<{items: Partial<Device>[]}>('SearchDevice', params);
  return result?.items;
};

/**
 * Retrieves a list of Assets based on the provided search filters.
 *
 * ```typescript
 * import {searchAssets} from '@ion/services/src/graphql/GraphQL';
 *
 * const assets = await searchAssets({
 *   search : {
 *     filter : {name: 'ion'}
 *   },
 *   fields: ['id', 'name', 'assetType']
 * });
 * ```
 *
 * @see     {@link SearchAssetsParams}
 * @see     {@link Asset}
 * @param   params An object containing the fields to be returned in the response,
 *          and the search criteria from which the results are to be retrieved.
 * @returns A Promise containing an array of matching `Asset` entities.
 */
export const searchAssets = async(
  params: SearchAssetsParams): Promise<Partial<Asset[]>> => {
  const result = await queryRequest<{items: Partial<Asset>[]}>('SearchAsset', params);
  return result?.items;
};

/**
 * Retrieves a list of Groups based on the provided search filters.
 *
 * ```typescript
 * import {searchGroups} from '@ion/services/src/graphql/GraphQL';
 *
 * const groups = await searchGroups({
 *   search : {
 *     filter : {name: 'ion'}
 *   },
 *   fields: ['id', 'name']
 * });
 * ```
 *
 * @see     {@link SearchGroupsParams}
 * @see     {@link Group}
 * @param   params An object containing the fields to be returned in the response,
 *          and the search criteria from which the results are to be retrieved.
 * @returns A Promise containing an array of matching `Group` entities.
 */
export const searchGroups = async(
  params: SearchGroupsParams): Promise<Partial<Group[]>> => {
  const result = await queryRequest<{items: Partial<Group>[]}>('SearchGroup', params);
  return result?.items;
};

/**
 * Retrieves an Account by `id` and returns the requested fields for the `Account`.
 *
 * ```typescript
 * import {getAccount} from '@ion/services/src/graphql/GraphQL';
 *
 * const account = await getAccount({
 *   fields: ['id', 'name'],
 *   args : {
 *     id : 2
 *   }
 * });
 * ```
 *
 * @see     {@link GetAccountParams}
 * @see     {@link Account}
 * @param   params An object containing the id of the account to be retrieved, and the
 *          `Account` fields to be returned in the response,
 * @returns A Promise containing the given `Account` entity.
 */
export const getAccount = async(
  params: GetAccountParams): Promise<Partial<Account>> => {
  const result = await queryRequest<Partial<Account>>('AccountQuery', params);
  return result;
};

/**
 * Retrieves an Application by `applicationKey` and returns the requested fields
 * for the `Application`.
 *
 * ```typescript
 * import {getApplication} from '@ion/services/src/graphql/GraphQL';
 *
 * const application = await getApplication({
 *   fields : ['id', 'name'],
 *   args   : {
 *     applicationKey : '82c904bd-71a8-4d38-a645-e6980510b7e2'
 *   }
 * });
 * ```
 *
 * @see     {@link GetApplicationParams}
 * @see     {@link Application}
 * @param   params An object containing the `applicationKey` of the application
 *          to be retrieved, and the`Application` fields to be returned in
 *          the response,
 * @returns A Promise containing the given `Application` entity.
 */
export const getApplication = async(
  params: GetApplicationParams): Promise<Partial<Application>> => {
  const result = await queryRequest<Partial<Application>>('ApplicationQuery', params);
  return result;
};

/**
 * Allows for creating a combined query based on the given Query types and parameters.
 *
 * ```typescript
 *   // create a combined `AccountQuery`, `ApplicationQuery`, and `CurrentUser` query
 *   const results = await combinedQuery({
 *     AccountQuery: {
 *       args : {
 *         id : 2
 *       },
 *       fields : ['id', 'name', {
 *         address : ['street', 'city']
 *       }]
 *     },
 *     ApplicationQuery: {
 *       args : {
 *         applicationKey : '82c904bd-71a8-4d38-a645-e6980510b7e2'
 *       },
 *       fields : ['id', 'name', {
 *         roles : {
 *           paging : {
 *             first  : 10,
 *             offset : 0
 *           },
 *           data : ['name', 'parentId']
 *        }
 *       }]
 *     },
 *     CurrentUser: {
 *       args : {
 *         id : 2
 *       },
 *       fields : ['id', 'username', {
 *         extendedAttribute : ['value']
 *       }]
 *     }
 *   });
 *   console.log(results?.AccountQuery); // account object with the requested fields
 *   console.log(results?.ApplicationQuery); // application object with the requested fields
 *   console.log(results?.CurrentUser); // user object with the requested fields
 * ```
 *
 * @see     {@link Query}
 * @see     {@link CreateQueryParams}
 * @see     {@link CreateCombinedQueryParams}
 * @see     {@link createCombinedQuery}
 * @param   queries An object whereby each key is a property name from the `Query`
 *          type, and each value is a `CreateQueryParams` object.
 * @returns A Promise containing the results of the combined query.
 */
export const combinedQuery = async(queries: CreateCombinedQueryParams): Promise<any> => (
  await request(null, queries, {isCombined: true})
);

/**
 * @private
 *
 * Provides a convenience from which GraphQL Queries are to be made. This function
 * simply delegates to the `request` function.
 *
 * @see     {@link Query}
 * @param   query The `Query` for which the request is to be made.
 * @param   params The fields which are to be returned in the response.
 */
const queryRequest = async<T>(
  query: keyof Query, params: unknown, generator?: Function): Promise<T> => (
  await request(query, params, {generator})
);

/**
 * @private
 *
 * Provides a convenience from which GraphQL Mutations are to be made. This function
 * simply delegates to the `request` function.
 *
 * @see     {@link Mutation}
 * @see     {@link GenericObject}
 * @param   mutation The `Mutation` for which the request is to be made.
 * @param   params The arguments to be included in the request, and the fields
 *          which are to be returned in the response.
 */
const mutationRequest = async<T>(
  mutation: keyof Mutation, params: GenericObject): Promise<T> => (
  await request(mutation, params, {isMutation: true})
);

/**
 * Provides the request options which allow for configuring a request as a mutation
 * or combined query.
 */
type RequestOptions = {
  isMutation?: boolean
  isCombined?: boolean
  generator?: Function
}

/**
 * @private
 *
 * Provides an abstraction from which all GraphQL Queries are issued against the
 * GraphQL client. This function allows for centralized management of requests,
 * resolving responses, etc.
 *
 * @see     {@link Query}
 * @see     {@link Mutation}
 * @see     {@link RequestError}
 * @param   operation The Query for which the request is to be made.
 * @param   params The fields which are to be returned by the request.
 * @param   isMutation When true, a mutation will be generated. Defaults to false,
 *          i.e. a Query will be generated by default.
 */
const request = async<T>(
  operation?: keyof Query | keyof Mutation, params?: unknown, {
    generator,
    isMutation = false,
    isCombined = false
  }: RequestOptions = {}): Promise<T> => {
  let document;
  if (isMutation) {
    document = createMutation(operation as keyof Mutation, params as any);
  } else if (!isCombined) {
    document = createQuery(operation as keyof Query, params as any, generator as Function);
  } else {
    document = createCombinedQuery(params);
  }
  try {
    const result = await getClient()?.request<T>(document);
    logger.response(result);
    if (!isCombined && operation) {
      return result?.[operation];
    }
    return result;
  } catch (error: any) {
    logger.error(error);
    return Promise.reject({
      results    : error.response?.data,
      errors     : error.response?.errors,
      cause      : error,
      statusCode : error.response?.status
    });
  }
};

/**
 * @internal
 *
 * The `rawQuery` API provides maximum flexibility for creating complex custom
 * Queries, but does so at the expense of the type-safety provided by the more
 * specific Queries.
 *
 * This API should be used sparingly and developers are to use their best judgement
 * as to when it should be used.
 *
 * Essentially, if a query is readily available, such as the `searchRoles` API, etc.
 * then the specific API should always be leveraged. Likewise, if a more complex
 * Query can be achieved via the `combinedQuery` API, it should always be used.
 *
 * @see {@link compress}
 *
 * @param   document The raw GraphQL Query string which is to be send in the request.
 * @returns The results of the Query Response.
 */
export const rawQuery = async<T>(document: string): Promise<T> => {
  try {
    const result = await getClient()?.request<T>(compress(gql`{${document}}`));
    logger.response(result);
    return result;
  } catch (error: any) {
    logger.error(error);
    return Promise.reject({
      results : error.response?.data,
      errors  : error.response?.errors,
      cause   : error
    });
  }
};
